/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, ChevronDown, Mail, MapPin, Sparkles, Linkedin } from "lucide-react";
import axios from "axios";
import {
  IMAGES,
  LOGO_URL,
  HEADSHOT_URL,
  CHRYSLER_URL,
  LINKEDIN_URL,
  BRIDGE_STORY,
  BUSINESS_TYPES,
  TIERS,
  WHY_ITEMS,
  ADDONS,
  STEPS,
  PORTFOLIO,
  TESTIMONIALS,
  FAQS,
  BUSINESS_TYPE_OPTIONS,
  NAV_LINKS,
} from "@/lib/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ---------- Banner (Chrysler / logo) ---------- */
function Banner() {
  return (
    <section
      id="top"
      data-testid="chrysler-banner"
      className="relative w-full overflow-hidden"
      style={{ height: "min(78vh, 720px)" }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0">
        <img
          src={CHRYSLER_URL}
          alt="Chrysler Building, New York City"
          className="w-full h-full object-cover"
          style={{ objectPosition: "center 30%" }}
          data-testid="banner-chrysler-img"
        />
        {/* Warm cinematic overlay */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(26,25,24,0.55) 0%, rgba(26,25,24,0.25) 35%, rgba(26,25,24,0.55) 100%)",
          }}
        />
        {/* Rust vignette */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 50% 60%, rgba(180,83,48,0.18) 0%, transparent 60%)",
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 text-center">
        {/* Ornament rule */}
        <div className="flex items-center gap-4 mb-8">
          <span className="h-px w-16 md:w-24 bg-cream/60" />
          <span className="text-cream/80 text-xl">✦</span>
          <span className="h-px w-16 md:w-24 bg-cream/60" />
        </div>

        <p className="eyebrow text-cream/70 mb-4" style={{ letterSpacing: "0.35em" }}>
          Marketing for the Moment
        </p>

        <h1
          className="font-display italic text-cream text-5xl md:text-7xl lg:text-[92px] leading-[0.95] max-w-4xl"
          data-testid="banner-tagline"
        >
          Beautiful websites,<br />made by hand.
        </h1>

        <p className="mt-6 text-cream/75 text-xs md:text-sm tracking-[0.35em] uppercase">
          By Tomorrow · North America
        </p>

        <div className="mt-10 flex items-center gap-4">
          <span className="h-px w-10 bg-cream/50" />
          <a
            href="#tiers"
            data-testid="banner-cta"
            className="text-cream/90 text-xs md:text-sm tracking-[0.3em] uppercase hover:text-cream link-underline"
          >
            See the three tiers
          </a>
          <span className="h-px w-10 bg-cream/50" />
        </div>
      </div>

      {/* Bottom fade to cream */}
      <div
        className="absolute bottom-0 inset-x-0 h-24 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(247,244,235,0) 0%, rgba(247,244,235,1) 100%)",
        }}
      />
    </section>
  );
}

/* ---------- Nav ---------- */
export function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const forceScrolled = location.pathname !== "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 120);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isSolid = scrolled || forceScrolled;
  const linkColor = isSolid ? "text-ink2 hover:text-ink" : "text-cream/85 hover:text-cream";
  const wordmarkColor = isSolid ? "text-ink2" : "text-cream/90";

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        isSolid ? "bg-cream/95 backdrop-blur-md border-b border-line" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
        <Link to="/" data-testid="logo-wordmark" className="flex items-center gap-3 group">
          <div
            className={`relative overflow-hidden rounded-full w-16 h-16 md:w-20 md:h-20 flex-shrink-0 transition-all duration-500 group-hover:-rotate-6 ${
              isSolid ? "" : "drop-shadow-[0_4px_14px_rgba(0,0,0,0.4)]"
            }`}
            data-testid="nav-logo"
          >
            <img
              src={LOGO_URL}
              alt="Marketing for the Moment"
              className="w-full h-full object-cover"
            />
          </div>
          <span className={`hidden lg:inline font-display italic text-lg leading-none transition-colors ${wordmarkColor}`}>
            Marketing for the Moment
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => {
            const cls = `text-[12px] uppercase tracking-[0.22em] transition-colors ${linkColor} link-underline`;
            const testId = `nav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`;
            if (l.href.startsWith("/") && !l.href.startsWith("/#")) {
              return (
                <Link key={l.href} to={l.href} data-testid={testId} className={cls}>
                  {l.label}
                </Link>
              );
            }
            return (
              <a key={l.href} href={l.href} data-testid={testId} className={cls}>
                {l.label}
              </a>
            );
          })}
          <a
            href="/#contact"
            data-testid="nav-start"
            className={`inline-flex items-center gap-2 px-6 py-3 text-[12px] tracking-[0.15em] uppercase font-medium transition-all ${
              isSolid
                ? "bg-ink text-cream hover:bg-rust"
                : "bg-cream text-ink hover:bg-rust hover:text-cream"
            }`}
          >
            Start Your Site <ArrowRight size={14} />
          </a>
        </nav>

        <button
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          className={`md:hidden p-2 -mr-2 ${isSolid ? "text-ink" : "text-cream"}`}
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-cream border-t border-line px-6 py-6 space-y-4" data-testid="mobile-menu">
          {NAV_LINKS.map((l) => {
            if (l.href.startsWith("/") && !l.href.startsWith("/#")) {
              return (
                <Link key={l.href} to={l.href} onClick={() => setOpen(false)} className="block text-lg font-display text-ink">
                  {l.label}
                </Link>
              );
            }
            return (
              <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="block text-lg font-display text-ink">
                {l.label}
              </a>
            );
          })}
          <a href="/#contact" onClick={() => setOpen(false)} className="btn-primary mt-4 inline-flex">
            Start Your Site <ArrowRight size={16} />
          </a>
        </div>
      )}
    </header>
  );
}

/* ---------- Hero ---------- */
function Hero() {
  return (
    <section className="relative pt-16 md:pt-20 pb-20 md:pb-24 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 items-end">
        <div className="lg:col-span-7">
          <p className="eyebrow reveal" data-delay="1">A brilliant website. Running 24/7.</p>
          <h1
            data-testid="hero-headline"
            className="font-display text-[52px] md:text-[92px] leading-[0.95] tracking-tight text-ink mt-6 reveal"
            data-delay="2"
          >
            Your business
            <br />
            <span className="italic">online by tomorrow.</span>
            <br />
            <span className="text-ink2">Personally designed.</span>
          </h1>

          <p className="mt-8 max-w-xl text-lg md:text-[19px] leading-relaxed text-ink2 reveal" data-delay="3">
            Three thoughtful tiers, three real prices — starting at{" "}
            <span className="text-ink font-medium">$750 for a same-day site</span> I design myself.
            No monthly fees, no platform traps, no missed phone calls about your hours.
            Just a website that quietly works for you, day and night.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4 reveal" data-delay="4">
            <a href="#tiers" data-testid="hero-see-tiers" className="btn-secondary">
              See the three tiers <ArrowRight size={16} />
            </a>
            <a href="#contact" data-testid="hero-start-tomorrow" className="btn-rust">
              Start tomorrow — $750 <ArrowRight size={16} />
            </a>
          </div>

          <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-sm text-ink2 reveal" data-delay="5">
            <span className="ornament">25+ years of marketing experience</span>
            <span className="ornament">One-time prices · No subscriptions</span>
          </div>
        </div>

        <div className="lg:col-span-5 reveal" data-delay="3">
          <div className="relative">
            <div className="absolute -top-6 -left-6 w-24 h-24 border border-rust/50 hidden md:block" aria-hidden="true" />
            <img
              src={IMAGES.hero}
              alt="A fancy desk overlooking the water"
              className="w-full h-[420px] md:h-[540px] object-cover"
              data-testid="hero-image"
            />
            <div className="absolute -bottom-5 -right-5 bg-cream border border-line px-5 py-4 max-w-[220px]">
              <p className="eyebrow">Live in 24 hours</p>
              <p className="font-display text-xl italic text-ink mt-1">From hello to hello.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Tiers strip ---------- */
function TiersStrip() {
  return (
    <section className="border-y border-line bg-parchment">
      {/* Logo intro band — sits right under the desk image from the hero above */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-14 pb-6 flex flex-col items-center text-center" data-testid="tiers-logo-band">
        <div className="flex items-center gap-6 w-full max-w-3xl">
          <span className="h-px flex-1 bg-line" />
          <div className="relative overflow-hidden rounded-full border border-line w-24 h-24 md:w-28 md:h-28 flex-shrink-0">
            <img
              src={LOGO_URL}
              alt="Marketing for the Moment"
              className="w-full h-full object-cover"
            />
          </div>
          <span className="h-px flex-1 bg-line" />
        </div>
        <p className="mt-5 font-display italic text-lg md:text-xl text-ink2">
          Three ways to work together.
        </p>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pb-6 pt-6 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-0 md:divide-x md:divide-line">
        <div className="md:pr-10">
          <p className="eyebrow">Three Tiers</p>
          <h3 className="font-display text-4xl md:text-5xl text-ink mt-2 italic">Tomorrow</h3>
          <p className="text-ink2 mt-2 text-sm uppercase tracking-widest">Same-day, by me</p>
          <p className="font-display text-3xl text-rust mt-4">$750</p>
        </div>
        <div className="md:px-10">
          <p className="eyebrow">Most popular</p>
          <h3 className="font-display text-4xl md:text-5xl text-ink mt-2 italic">Coffee & a Plan</h3>
          <p className="text-ink2 mt-2 text-sm uppercase tracking-widest">Story, Strategy, Growth</p>
          <p className="font-display text-3xl text-rust mt-4">$1,500</p>
        </div>
        <div className="md:pl-10">
          <p className="eyebrow">Signature</p>
          <h3 className="font-display text-4xl md:text-5xl text-ink mt-2 italic">Cinematic</h3>
          <p className="text-ink2 mt-2 text-sm uppercase tracking-widest">Fully custom · Passion conveyed</p>
          <p className="font-display text-3xl text-rust mt-4">$3,000</p>
        </div>
      </div>
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pb-10 flex flex-wrap items-center justify-between gap-4">
        <a href="#tiers" className="text-sm uppercase tracking-[0.2em] text-ink link-underline" data-testid="strip-compare">
          Compare what's inside →
        </a>
        <span className="text-sm uppercase tracking-[0.2em] text-ink2">One-time</span>
      </div>
    </section>
  );
}

/* ---------- Marquee ---------- */
function Marquee() {
  const items = [...BUSINESS_TYPES, ...BUSINESS_TYPES];
  return (
    <section aria-label="Business types" className="py-14 border-b border-line overflow-hidden bg-cream">
      <div className="marquee-track marquee-inner">
        {items.map((t, i) => (
          <span
            key={i}
            className="flex items-center gap-8 px-6 font-display italic text-4xl md:text-6xl text-ink whitespace-nowrap"
          >
            {t}
            <span className="text-rust">✦</span>
          </span>
        ))}
      </div>
    </section>
  );
}

/* ---------- About Andrea (brief summary) ---------- */
function About() {
  return (
    <section id="about" className="py-24 md:py-32">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="flex flex-col items-center lg:items-start gap-5">
              <div className="relative">
                <div
                  className="relative w-[160px] h-[160px] rounded-full overflow-hidden border border-line"
                  style={{ backgroundColor: "#F0EBE1" }}
                  data-testid="headshot-frame"
                >
                  <img
                    src={HEADSHOT_URL}
                    alt="Andrea Brown, Founder"
                    data-testid="andrea-headshot"
                    className="w-full h-full object-cover"
                    style={{
                      mixBlendMode: "multiply",
                      objectPosition: "center 20%",
                      filter: "blur(0.5px) contrast(0.92) brightness(1.04) saturate(0.88)",
                    }}
                  />
                </div>
                <span
                  className="absolute inset-0 rounded-full border border-rust/40 pointer-events-none"
                  style={{ transform: "translate(8px, 8px)" }}
                  aria-hidden="true"
                />
              </div>

              <div className="text-center lg:text-left">
                <p className="eyebrow leading-none" style={{ fontSize: "10px" }}>Andrea Brown</p>
                <p className="font-display italic text-lg text-ink leading-tight mt-1">Founder & Designer</p>
                <a
                  href={LINKEDIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="home-linkedin"
                  className="mt-3 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.2em] text-ink2 hover:text-rust transition-colors"
                >
                  <Linkedin size={14} /> Connect on LinkedIn
                </a>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <p className="eyebrow">Hi, I'm Andrea</p>
            <h2 className="font-display text-5xl md:text-6xl text-ink mt-4 leading-[0.95]">
              I know your <span className="italic text-rust">ride home.</span>
            </h2>

            <div className="mt-8 space-y-5 text-[17px] leading-[1.75] text-ink2 max-w-2xl">
              <p>
                Twenty-five years of marketing — for a Major League Baseball club, a Fortune 100 tech giant, and an American quick-service restaurant empire I married into. But, the marketing I care about the most is the kind that will help you grow and provides your customers with the best experience. Because communities do not exist and thrive without people like you.
              </p>
              <p className="font-display italic text-xl text-ink">
                Your website should help you breathe a little easier. That's the whole point of mine.
              </p>
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/about"
                data-testid="about-read-more"
                className="btn-secondary"
              >
                Read Andrea's story <ArrowRight size={16} />
              </Link>
              <a href="#contact" data-testid="about-contact" className="btn-rust">
                Start the conversation <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Bridge (Corporate → Family) ---------- */
function Bridge() {
  const s = BRIDGE_STORY;
  return (
    <section id="bridge" className="relative py-24 md:py-36 bg-ink text-cream overflow-hidden">
      {/* Subtle grain */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(rgba(247,244,235,0.9) 1px, transparent 1px), radial-gradient(rgba(247,244,235,0.5) 1px, transparent 1px)",
          backgroundSize: "3px 3px, 7px 7px",
          backgroundPosition: "0 0, 1px 1px",
        }}
      />

      <div className="relative max-w-[1200px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          {/* LEFT — headline */}
          <div className="lg:col-span-5">
            <p className="eyebrow" style={{ color: "#E29A78" }}>{s.eyebrow}</p>
            <h2 className="font-display text-5xl md:text-[80px] leading-[0.95] tracking-tight mt-6">
              {s.headline_1}
              <br />
              <span className="italic" style={{ color: "#E29A78" }}>{s.headline_2}</span>
            </h2>

            <div className="mt-10 space-y-2">
              {s.chips.map((c, i) => (
                <div
                  key={i}
                  data-testid={`bridge-chip-${i}`}
                  className="inline-flex items-center gap-3 mr-2 mb-2 border border-cream/25 px-4 py-2"
                >
                  <span className="text-rust text-xs">✦</span>
                  <span className="text-[13px] uppercase tracking-[0.2em] text-cream/85">{c}</span>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — narrative */}
          <div className="lg:col-span-7 space-y-6 text-[17px] leading-[1.85] text-cream/80">
            {s.paragraphs.map((p, i) => (
              <p key={i} className={i === 1 ? "font-display italic text-2xl md:text-3xl text-cream leading-snug py-2" : ""}>
                {p}
              </p>
            ))}

            <div className="my-10 flex items-center gap-6">
              <span className="h-px flex-1 bg-rust/60" />
              <p className="font-display italic text-3xl md:text-4xl" style={{ color: "#E29A78" }}>
                {s.pullquote}
              </p>
              <span className="h-px flex-1 bg-rust/60" />
            </div>

            {s.paragraphs_after.map((p, i) => (
              <p key={i}>{p}</p>
            ))}

            <div className="pt-8 mt-4 border-t border-cream/15">
              <p className="font-display italic text-3xl md:text-4xl text-cream leading-tight">
                {s.closing_line_1}
              </p>
              <p className="font-display italic text-3xl md:text-4xl leading-tight mt-1" style={{ color: "#E29A78" }}>
                {s.closing_line_2}
              </p>
              <p className="mt-6 text-sm tracking-[0.3em] uppercase text-cream/60">{s.signoff}</p>
            </div>

            <div className="pt-6">
              <a
                href="#tiers"
                data-testid="bridge-cta"
                className="inline-flex items-center gap-3 bg-rust text-cream px-7 py-4 text-sm uppercase tracking-[0.15em] transition-all hover:bg-cream hover:text-ink"
              >
                See how we build it together <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Tier Details ---------- */
function TierDetails() {
  return (
    <section id="tiers" className="py-24 md:py-36 bg-parchment border-y border-line">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <p className="eyebrow">Three Tiers · Three Real Prices</p>
          <h2 className="font-display text-5xl md:text-7xl text-ink mt-4 leading-[0.95]">
            Pick the tier<br />
            <span className="italic">that fits your moment.</span>
          </h2>
          <p className="text-ink2 mt-6 text-lg leading-relaxed">
            Every tier is one-time. No subscriptions, no contracts, no being held hostage by a platform.
            The only difference is how deep we go together — and how cinematic it gets.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {TIERS.map((t) => (
            <article
              key={t.id}
              data-testid={`tier-card-${t.id}`}
              className={`relative p-8 md:p-10 border transition-all duration-500 ${
                t.highlighted
                  ? "bg-ink text-cream border-ink -translate-y-2 shadow-[8px_8px_0_0_#B45330]"
                  : "bg-cream border-line hover:border-rust"
              }`}
            >
              {t.highlighted && (
                <span className="absolute -top-3 left-8 bg-rust text-cream text-[10px] tracking-[0.25em] uppercase px-3 py-1">
                  Most popular
                </span>
              )}
              <p className={`eyebrow ${t.highlighted ? "text-rust" : ""}`}>{t.name}</p>
              <h3 className={`font-display text-4xl md:text-5xl mt-3 italic ${t.highlighted ? "text-cream" : "text-ink"}`}>
                {t.subhead}
              </h3>
              <div className="mt-6 flex items-baseline gap-3">
                <span className={`font-display text-5xl ${t.highlighted ? "text-cream" : "text-ink"}`}>{t.priceLabel}</span>
                <span className={`text-sm ${t.highlighted ? "text-cream/70" : "text-ink2"}`}>· one-time</span>
              </div>
              <p className={`mt-2 text-sm uppercase tracking-[0.2em] ${t.highlighted ? "text-rust" : "text-rust"}`}>
                {t.timeline}
              </p>
              <p className={`mt-6 text-[17px] leading-relaxed font-display italic ${t.highlighted ? "text-cream" : "text-ink"}`}>
                {t.tagline}
              </p>
              <p className={`mt-4 text-[15px] leading-relaxed ${t.highlighted ? "text-cream/80" : "text-ink2"}`}>
                {t.description}
              </p>

              <ul className={`mt-8 space-y-3 text-[15px] leading-relaxed ${t.highlighted ? "text-cream/90" : "text-ink"}`}>
                {t.features.map((f, i) => (
                  <li key={i} className="flex gap-3">
                    <span className={`mt-2 h-[1px] w-4 flex-shrink-0 ${t.highlighted ? "bg-rust" : "bg-rust"}`} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <a
                href="#contact"
                data-testid={`tier-cta-${t.id}`}
                className={`mt-10 inline-flex items-center gap-2 py-4 px-6 uppercase tracking-[0.15em] text-sm font-medium transition-all ${
                  t.highlighted
                    ? "bg-rust text-cream hover:bg-cream hover:text-ink"
                    : "bg-ink text-cream hover:bg-rust"
                }`}
              >
                {t.cta} <ArrowRight size={16} />
              </a>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Why Worth It ---------- */
function WhyWorthIt() {
  return (
    <section className="py-24 md:py-36">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <p className="eyebrow">Why It's Worth It</p>
          <h2 className="font-display text-5xl md:text-7xl text-ink mt-4 leading-[0.95]">
            Every tier removes<br />
            <span className="italic">an instant problem.</span>
          </h2>
          <p className="text-ink2 mt-6 text-lg leading-relaxed">
            Each price point pays for itself the moment you stop fielding rote phone calls and start letting your website do the talking.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
          {WHY_ITEMS.map((w, i) => (
            <div key={i} className="card-cream p-10" data-testid={`why-${i}`}>
              <span className="big-num">0{i + 1}</span>
              <h3 className="font-display text-2xl md:text-3xl text-ink mt-4 leading-tight">{w.title}</h3>
              <p className="text-ink2 mt-3 text-[15px] leading-relaxed">{w.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Add-Ons ---------- */
function AddOns() {
  return (
    <section className="py-24 md:py-32 bg-parchment border-y border-line">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-5">
            <p className="eyebrow">Optional Add-Ons</p>
            <h2 className="font-display text-5xl md:text-6xl text-ink mt-4 leading-[0.95]">
              The tools that <span className="italic">do the work for you.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 text-ink2 text-lg leading-relaxed">
            Most of these are baked into the Coffee & a Plan and Cinematic tiers. Want one or two added to a Same-Day Site?
            Just ask — I'll quote it honestly.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {ADDONS.map((a, i) => (
            <div key={i} className="card-cream p-8" data-testid={`addon-${i}`}>
              <Sparkles size={18} className="text-rust" />
              <h3 className="font-display text-2xl text-ink mt-4">{a.title}</h3>
              <p className="text-ink2 mt-2 text-[15px] leading-relaxed">{a.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- How It Works ---------- */
function HowItWorks() {
  return (
    <section className="py-24 md:py-36">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <p className="eyebrow">How It Works</p>
          <h2 className="font-display text-5xl md:text-7xl text-ink mt-4 leading-[0.95]">
            From hello to launch <span className="italic">in as little as 24 hours.</span>
          </h2>
        </div>

        <ol className="mt-16 space-y-10">
          {STEPS.map((s, i) => (
            <li key={s.n} className="grid grid-cols-12 gap-6 pb-10 border-b border-line last:border-b-0" data-testid={`step-${s.n}`}>
              <div className="col-span-3 md:col-span-2">
                <span className="big-num">{s.n}</span>
              </div>
              <div className="col-span-9 md:col-span-10">
                <h3 className="font-display text-3xl md:text-4xl text-ink italic">{s.title}</h3>
                <p className="text-ink2 mt-3 text-[17px] leading-relaxed max-w-2xl">{s.body}</p>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-12">
          <a href="#tiers" className="btn-primary" data-testid="how-pick-tier">
            Let's pick your tier <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </section>
  );
}

/* ---------- Logo Mark (decorative section-break) ---------- */
function LogoMark({ size = "md", tagline, testId = "logo-mark" }) {
  const sizeClass = size === "lg" ? "w-40 h-40 md:w-56 md:h-56" : "w-28 h-28 md:w-36 md:h-36";
  return (
    <div className="py-16 md:py-20 flex flex-col items-center text-center" data-testid={testId}>
      <div className="flex items-center gap-4 mb-6">
        <span className="h-px w-14 md:w-24 bg-line" />
        <span className="text-rust text-lg">✦</span>
        <span className="h-px w-14 md:w-24 bg-line" />
      </div>
      <div className={`relative overflow-hidden rounded-full border border-line ${sizeClass}`}>
        <img
          src={LOGO_URL}
          alt="Marketing for the Moment"
          className="w-full h-full object-cover"
        />
      </div>
      {tagline && (
        <p className="mt-6 font-display italic text-xl md:text-2xl text-ink2 max-w-md leading-snug">
          {tagline}
        </p>
      )}
    </div>
  );
}

/* ---------- Portfolio ---------- */
function Portfolio() {
  return (
    <section id="work" className="py-24 md:py-36 bg-parchment border-y border-line">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-5">
            <p className="eyebrow">Selected Work</p>
            <h2 className="font-display text-5xl md:text-6xl text-ink mt-4 leading-[0.95]">
              The kind of sites <span className="italic">I love to make.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 text-ink2 text-lg leading-relaxed">
            A taste of the kinds of businesses I build for. Each site is custom — no two ever the same.
            Real client work shown with permission, plus illustrative samples.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PORTFOLIO.map((p, i) => (
            <a
              key={i}
              href="#contact"
              data-testid={`portfolio-${p.img}`}
              className={`group block ${i === 0 ? "lg:col-span-2" : ""}`}
            >
              <div className="relative overflow-hidden bg-ink">
                <img
                  src={IMAGES.portfolio[p.img]}
                  alt={p.name}
                  className={`w-full ${i === 0 ? "h-[420px]" : "h-[320px]"} object-cover transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0 grayscale`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 text-cream">
                  <p className="eyebrow" style={{ color: "#F0EBE1" }}>{p.type}</p>
                  <h3 className="font-display text-3xl italic mt-1">{p.name}</h3>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Testimonials ---------- */
function Testimonials() {
  return (
    <section id="kind-words" className="py-24 md:py-36">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <p className="eyebrow">Kind Words</p>
          <h2 className="font-display text-5xl md:text-7xl text-ink mt-4 leading-[0.95]">
            From the people <span className="italic">who said yes.</span>
          </h2>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <figure key={i} className="card-cream p-10" data-testid={`testimonial-${i}`}>
              <span className="font-display italic text-6xl text-rust leading-none">"</span>
              <blockquote className="font-display text-xl md:text-[22px] text-ink italic leading-snug mt-2">
                {t.quote}
              </blockquote>
              <figcaption className="mt-8 pt-6 border-t border-line">
                <p className="font-display text-lg text-ink">{t.name}</p>
                <p className="text-ink2 text-sm mt-1">{t.role}</p>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- FAQ ---------- */
function FAQ() {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <section id="faq" className="py-24 md:py-36 bg-parchment border-y border-line">
      <div className="max-w-[1000px] mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <p className="eyebrow">Questions, Answered</p>
          <h2 className="font-display text-5xl md:text-7xl text-ink mt-4 leading-[0.95]">
            Things people <span className="italic">wonder about.</span>
          </h2>
          <p className="text-ink2 mt-6 text-lg">
            Can't find what you're after? Send me a note in the form below and I'll get back to you within one business day.
          </p>
        </div>

        <div className="mt-14">
          {FAQS.map((f, i) => {
            const isOpen = openIdx === i;
            return (
              <div key={i} className="border-b border-line" data-testid={`faq-${i}`}>
                <button
                  onClick={() => setOpenIdx(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between text-left py-6 group"
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="font-display italic text-xl md:text-2xl text-ink pr-8">{f.q}</span>
                  <ChevronDown
                    size={22}
                    className={`text-ink2 flex-shrink-0 transition-transform duration-500 ${isOpen ? "rotate-180 text-rust" : ""}`}
                  />
                </button>
                <div
                  className={`grid transition-all duration-500 ease-out ${isOpen ? "grid-rows-[1fr] opacity-100 pb-8" : "grid-rows-[0fr] opacity-0"}`}
                >
                  <div className="overflow-hidden">
                    <p className="text-ink2 text-[16px] leading-[1.75] max-w-3xl">{f.a}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------- Contact ---------- */
function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    business_name: "",
    business_type: "",
    message: "",
    tier_interest: "",
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus({ state: "error", msg: "Please fill in your name, email, and a short note." });
      return;
    }
    setStatus({ state: "loading", msg: "Sending your note…" });
    try {
      const payload = {
        ...form,
        business_type: form.business_type === "Choose one" ? "" : form.business_type,
      };
      await axios.post(`${API}/contact`, payload);
      setStatus({
        state: "success",
        msg: "Thank you — your note is on its way. Andrea will reply within one business day.",
      });
      setForm({ name: "", email: "", business_name: "", business_type: "", message: "", tier_interest: "" });
    } catch (err) {
      setStatus({
        state: "error",
        msg: err?.response?.data?.detail || "Something went sideways. Please try again in a moment.",
      });
    }
  };

  return (
    <section id="contact" className="py-24 md:py-36">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <p className="eyebrow">Start the Conversation</p>
          <h2 className="font-display text-5xl md:text-7xl text-ink mt-4 leading-[0.95]">
            Tell me about<br /><span className="italic">your business.</span>
          </h2>
          <p className="text-ink2 mt-6 text-lg leading-relaxed">
            Send a note — even just a couple of sentences. I read every one personally and reply within one business day.
          </p>

          <div className="mt-12 space-y-6">
            <div>
              <p className="eyebrow">Direct</p>
              <a href="mailto:andrea@marketingforthemoment.com" className="mt-2 font-display italic text-2xl text-ink flex items-center gap-3 link-underline">
                <Mail size={20} className="text-rust" />
                andrea@marketingforthemoment.com
              </a>
            </div>
            <div>
              <p className="eyebrow">Where</p>
              <p className="mt-2 font-display italic text-2xl text-ink flex items-center gap-3">
                <MapPin size={20} className="text-rust" />
                Working with businesses everywhere in North America
              </p>
            </div>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          noValidate
          className="lg:col-span-7 lg:col-start-6 space-y-6 bg-parchment border border-line p-8 md:p-12"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Your Name" name="name" value={form.name} onChange={handleChange} required testId="input-name" />
            <Field label="Email" name="email" type="email" value={form.email} onChange={handleChange} required testId="input-email" />
          </div>
          <Field label="Business Name" name="business_name" value={form.business_name} onChange={handleChange} testId="input-business-name" />

          <div>
            <label className="eyebrow block mb-2">Business Type</label>
            <select
              name="business_type"
              value={form.business_type}
              onChange={handleChange}
              data-testid="select-business-type"
              className="w-full bg-cream border border-line px-4 py-4 text-ink font-sans focus:outline-none focus:border-rust transition-colors"
            >
              {BUSINESS_TYPE_OPTIONS.map((o) => (
                <option key={o} value={o === "Choose one" ? "" : o}>{o}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="eyebrow block mb-2">Tell me a little about it</label>
            <textarea
              name="message"
              rows={5}
              value={form.message}
              onChange={handleChange}
              required
              data-testid="input-message"
              className="w-full bg-cream border border-line px-4 py-4 text-ink font-sans focus:outline-none focus:border-rust transition-colors resize-none"
              placeholder="A couple of sentences is plenty. What do you do, and what's the quiet problem you'd love your website to solve?"
            />
          </div>

          <p className="text-sm text-ink2 italic">I'll reply within one business day. Promise.</p>

          {status.state !== "idle" && (
            <div
              data-testid="form-status"
              className={`px-4 py-3 text-sm border ${
                status.state === "success"
                  ? "border-rust text-rust bg-cream"
                  : status.state === "error"
                  ? "border-red-700 text-red-800 bg-cream"
                  : "border-line text-ink2 bg-cream"
              }`}
            >
              {status.msg}
            </div>
          )}

          <button type="submit" data-testid="submit-contact" disabled={status.state === "loading"} className="btn-rust w-full justify-center disabled:opacity-60">
            {status.state === "loading" ? "Sending…" : "Send the note"} <ArrowRight size={16} />
          </button>
        </form>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, type = "text", required = false, testId }) {
  return (
    <div>
      <label className="eyebrow block mb-2">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testId}
        className="w-full bg-cream border border-line px-4 py-4 text-ink font-sans focus:outline-none focus:border-rust transition-colors"
      />
    </div>
  );
}

/* ---------- Footer ---------- */
export function Footer() {
  return (
    <footer className="border-t border-line bg-parchment">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 grid grid-cols-1 md:grid-cols-3 gap-10">
        <div>
          <div className="relative overflow-hidden rounded-full w-32 h-32 md:w-36 md:h-36" data-testid="footer-logo">
            <img
              src={LOGO_URL}
              alt="Marketing for the Moment"
              className="w-full h-full object-cover"
            />
          </div>
          <p className="text-ink2 text-sm mt-4 max-w-xs">Beautiful websites, made by hand.</p>
        </div>
        <div>
          <p className="eyebrow">Get in touch</p>
          <a href="mailto:andrea@marketingforthemoment.com" className="mt-3 block text-ink font-display italic text-lg link-underline">
            andrea@marketingforthemoment.com
          </a>
          <a href="/#contact" className="mt-3 block text-ink font-display italic text-lg link-underline">
            Start your site →
          </a>
          <a
            href={LINKEDIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-linkedin"
            className="mt-3 inline-flex items-center gap-2 text-ink font-display italic text-lg link-underline"
          >
            <Linkedin size={16} /> Andrea on LinkedIn
          </a>
        </div>
        <div>
          <p className="eyebrow">Explore</p>
          <div className="mt-3 space-y-2">
            {NAV_LINKS.map((l) => {
              const cls = "block text-ink font-display italic text-lg link-underline w-fit";
              if (l.href.startsWith("/") && !l.href.startsWith("/#")) {
                return (
                  <Link key={l.href} to={l.href} className={cls}>
                    {l.label}
                  </Link>
                );
              }
              return (
                <a key={l.href} href={l.href} className={cls}>
                  {l.label}
                </a>
              );
            })}
          </div>
        </div>
      </div>
      <div className="border-t border-line py-6 text-center text-sm text-ink2">
        © 2026 Marketing for the Moment · www.marketingforthemoment.com
      </div>
    </footer>
  );
}

/* ---------- Page ---------- */
export default function Home() {
  useEffect(() => {
    document.title = "Marketing for the Moment · A brilliant website, running 24/7.";
    // Scroll to hash target if present (e.g. arriving from /about via /#tiers)
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
      }
    }
  }, []);
  return (
    <div className="bg-cream text-ink">
      <Nav />
      <Banner />
      <Hero />
      <TiersStrip />
      <Marquee />
      <About />
      <Bridge />
      <TierDetails />
      <WhyWorthIt />
      <AddOns />
      <HowItWorks />
      <LogoMark size="md" tagline="Made by hand, made for the moment." testId="mid-logo-mark" />
      <Testimonials />
      <FAQ />
      <LogoMark size="lg" tagline="One conversation. One website. One quiet, working front door." testId="pre-contact-logo-mark" />
      <Contact />
      <Footer />
    </div>
  );
}
